import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contabilidadService } from '../services/contabilidadService';
import { KPIS_QUERY_KEY } from './useKpis';
import { CAJAS_QUERY_KEY } from './useCajas';
import { MOVIMIENTOS_QUERY_KEY } from './useMovimientos';
import { CHART_QUERY_KEY } from './useChartData';
import { EGRESOS_PENDIENTES_QUERY_KEY } from './useEgresosPendientes';
import type { ArbolEgresosPendientes, Moneda, PagoPayload } from '../types';

/** Cuelga de EGRESOS_PENDIENTES_QUERY_KEY: invalidar esa clave también refresca el árbol. */
export const ARBOL_EGRESOS_QUERY_KEY = [...EGRESOS_PENDIENTES_QUERY_KEY, 'arbol'] as const;

export function useArbolEgresos() {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery<ArbolEgresosPendientes>({
    queryKey: ARBOL_EGRESOS_QUERY_KEY,
    queryFn: () => contabilidadService.getArbolEgresos(),
    staleTime: 0,
  });

  const invalidar = () => {
    queryClient.invalidateQueries({ queryKey: EGRESOS_PENDIENTES_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: KPIS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: CAJAS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: MOVIMIENTOS_QUERY_KEY });
    queryClient.invalidateQueries({ queryKey: CHART_QUERY_KEY });
  };

  const pagarAgrupadorMutation = useMutation({
    mutationFn: ({ id, moneda, payload }: { id: number; moneda: Moneda; payload: PagoPayload }) =>
      contabilidadService.pagarAgrupador(id, moneda, payload),
    onSuccess: invalidar,
  });

  const pagarItemMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: PagoPayload }) =>
      contabilidadService.pagarEgreso(id, payload),
    onSuccess: invalidar,
  });

  return {
    arbol: data,
    isLoading,
    error,
    pagarAgrupador: pagarAgrupadorMutation.mutateAsync,
    pagarItem: pagarItemMutation.mutateAsync,
    isPagando: pagarAgrupadorMutation.isPending || pagarItemMutation.isPending,
  };
}
